import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { FlashcardScrollView } from "@-/dev.improve.haniverse.components.ui.card-scroll-view";
import { ScrollView, View } from "@-/com.npmjs.react-native";
import type { WordCharacterBack } from "@-/dev.improve.haniverse.lib.sentence.context";
import { GraphemeBackTile } from "#grapheme-back-tile";
import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useRef } from "react";

/**
 * The revealed side of a word card: the sentence that framed it, then the word broken into its
 * characters with each character's own back beneath its glyph.
 *
 * This is the SENTENCE pane — the context the word appeared in, with room for the user's own
 * notes. Details about the word itself live one swipe to the right, mirroring how a grapheme's
 * mnemonics pane sits beside its info pane.
 */
export function WordSentenceBackView({
  characterBacks,
  sentence,
  translation,
  word,
}: {
  characterBacks: readonly WordCharacterBack[];
  /** The Chinese sentence, already converted to the reader's script. Null when unavailable. */
  sentence: string | null;
  translation: string | null;
  word: string;
}) {
  const scrollY = useRouteBodyRef("word-sentence-back-scroll", 0);
  const scrollRef = useRef<ScrollView>(null);
  const restoredRef = useRef(false);
  return (
    <FlashcardScrollView
      className="flex-1 bg-background"
      contentContainerClassName="gap-5 px-4 pt-4 pb-6"
      onContentSizeChange={() => {
        if (restoredRef.current) {
          return;
        }
        restoredRef.current = true;
        scrollRef.current?.scrollTo({ animated: false, y: scrollY.current });
      }}
      onScroll={(event) => {
        if (restoredRef.current) {
          scrollY.current = event.nativeEvent.contentOffset.y;
        }
      }}
      ref={scrollRef}
      scrollEventThrottle={32}
      showsVerticalScrollIndicator={false}
    >
      {sentence === null ? null : (
        <View className="gap-2">
          <Text className="font-sans text-[24px] leading-9 text-foreground" colorVariant="default">
            {sentence}
          </Text>
          {translation === null ? null : (
            <Text className="font-sans text-[16px]" colorVariant="muted">
              {translation}
            </Text>
          )}
        </View>
      )}

      <View className="gap-2">
        <Text className="font-sans text-[13px] uppercase" colorVariant="muted">
          {word}
        </Text>
        <View className="flex-row flex-wrap gap-2">
          {characterBacks.map((characterBack, index) => (
            <GraphemeBackTile
              back={characterBack.back}
              grapheme={characterBack.grapheme}
              isTarget
              // Characters repeat within a word (每每), so the index is part of the identity —
              // keying on the glyph alone would collide.
              key={`${characterBack.grapheme}-${String(index)}`}
            />
          ))}
        </View>
      </View>
    </FlashcardScrollView>
  );
}
